import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './IDPIntegration.css';
import Navbar from '../../components/Navbar/Navbar';
import Aurora from '../../components/Aurora/Aurora';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import Footer from '../../components/Footer/Footer';

const IDPIntegration = () => {
    const [navbarHidden, setNavbarHidden] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [copiedField, setCopiedField] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const viewportHeight = window.innerHeight;

            if (currentScrollY > lastScrollY && currentScrollY > viewportHeight) {
                setNavbarHidden(true);
            } else {
                setNavbarHidden(false);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const endpoints = [
        {
            label: 'IdP Metadata URL',
            url: 'https://mydigitalschools.com/api/saml/metadata',
            description: 'Import this URL into your SP to automatically configure our IdP settings.'
        },
        {
            label: 'SSO Service Endpoint',
            url: 'https://mydigitalschools.com/api/saml/sso',
            description: 'The endpoint where SAML authentication requests are processed.'
        },
        {
            label: 'Entity ID',
            url: 'https://mydigitalschools.com/api/saml/metadata',
            description: 'Our unique identifier as an Identity Provider.'
        }
    ];

    const partnerChecklist = [
        'Provide your SP Metadata URL or XML file',
        'Provide your ACS (Assertion Consumer Service) URL',
        'Provide your Entity ID / SP Issuer',
        'Import our IdP Metadata or public certificate',
        'Configure attribute mapping to match our attributes',
        'Enable SSO in your platform settings',
        'Test with a sample user account'
    ];

    const mdsChecklist = [
        'Configure your SP details in our environment',
        'Set up ACS URL and Entity ID',
        'Generate or provide SAML signing keys',
        'Configure user attribute mappings',
        'Test end-to-end SSO flow',
        'Deploy to production'
    ];

    const attributes = [
        { name: 'mail', format: 'Email Address', example: 'user@school.edu', description: 'Primary email attribute' },
        { name: 'email', format: 'Email Address', example: 'user@school.edu', description: 'Duplicate for compatibility' },
        { name: 'firstName', format: 'String', example: 'John', description: 'User first name' },
        { name: 'lastName', format: 'String', example: 'Doe', description: 'User last name' },
        { name: 'role', format: 'String', example: 'student | teacher', description: 'User role mapping' }
    ];

    return (
        <div className="idp-page">
            <Navbar className={navbarHidden ? 'hidden' : ''} />

            {/* Aurora header */}
            <div className="idp-aurora hero-section">
                <div className="aurora-wrapper">
                    <Aurora
                        colorStops={["#0088FF", "#4ecdc4", "#1E40AF"]}
                        blend={0.8}
                        amplitude={1.2}
                        speed={0.3}
                    />
                </div>
            </div>

            {/* Hero Section */}
            <section className="idp-hero hero-section">
                <div className="idp-hero-container">
                    <div className="idp-hero-content">
                        <Link to="/integrations" className="back-link">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Back to Integrations
                        </Link>
                        <h1 className="idp-hero-title">SAML 2.0 Identity Provider Integration</h1>
                        <p className="idp-hero-subtitle">
                            MyDigitalSchools acts as the Identity Provider (IdP). Configure your Service Provider (SP)
                            to accept our SAML assertions for seamless single sign-on.
                        </p>
                    </div>
                </div>
            </section>

            {/* Production Endpoints */}
            <section className="idp-section">
                <div className="idp-container">
                    <div className="section-header">
                        <h2>Production Endpoints</h2>
                        <p>(Use these URLs to configure your Service Provider.)</p>
                    </div>

                    <div className="endpoint-grid">
                        {endpoints.map((endpoint, idx) => (
                            <div key={idx} className="endpoint-card">
                                <div className="endpoint-label">{endpoint.label}</div>
                                <div className="endpoint-url">
                                    <code>{endpoint.url}</code>
                                    <button
                                        className={`copy-btn ${copiedField === endpoint.label ? 'copied' : ''}`}
                                        onClick={() => copyToClipboard(endpoint.url, endpoint.label)}
                                    >
                                        {copiedField === endpoint.label ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}>
                                    {endpoint.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Integration Checklists */}
            <section className="idp-section">
                <div className="idp-container">
                    <div className="section-header">
                        <h2>Integration Checklists</h2>
                        <p>Follow these steps to complete the SAML integration.</p>
                    </div>

                    <div className="checklist-grid">
                        <div className="checklist-card">
                            <h3>
                                <span className="badge partner-badge">Partner</span>
                                What You Need To Provide
                            </h3>
                            <ul className="checklist-items">
                                {partnerChecklist.map((item, idx) => (
                                    <li key={idx}>
                                        <span className="check-icon">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="checklist-card">
                            <h3>
                                <span className="badge mds-badge">MDS</span>
                                What We Configure
                            </h3>
                            <ul className="checklist-items">
                                {mdsChecklist.map((item, idx) => (
                                    <li key={idx}>
                                        <span className="check-icon">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Technical Specifications */}
            <section className="idp-section">
                <div className="idp-container">
                    <div className="section-header">
                        <h2>Technical Specifications</h2>
                        <p>Our SAML implementation details for your reference.</p>
                    </div>

                    <div className="specs-grid">
                        <div className="spec-item">
                            <h4>Signature Algorithm</h4>
                            <p>RSA-SHA256</p>
                        </div>
                        <div className="spec-item">
                            <h4>NameID Format</h4>
                            <p>Email Address</p>
                        </div>
                        <div className="spec-item">
                            <h4>Bindings Supported</h4>
                            <p>HTTP-POST, HTTP-Redirect</p>
                        </div>
                        <div className="spec-item">
                            <h4>Assertion Validity</h4>
                            <p>5 Minutes</p>
                        </div>
                        <div className="spec-item">
                            <h4>Flow Type</h4>
                            <p>IdP-Initiated</p>
                        </div>
                        <div className="spec-item">
                            <h4>Canonicalization</h4>
                            <p>xml-exc-c14n#</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Attributes */}
            <section className="idp-section">
                <div className="idp-container">
                    <div className="section-header">
                        <h2>User Attributes</h2>
                        <p>These attributes are included in the SAML assertion.</p>
                    </div>

                    <table className="attributes-table">
                        <thead>
                            <tr>
                                <th>Attribute Name</th>
                                <th>Format</th>
                                <th>Example</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attributes.map((attr, idx) => (
                                <tr key={idx}>
                                    <td><code>{attr.name}</code></td>
                                    <td>{attr.format}</td>
                                    <td>{attr.example}</td>
                                    <td>{attr.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* OAuth2 / OIDC Section */}
            <section className="idp-section">
                <div className="idp-container">
                    <div className="section-header">
                        <h2>OAuth2 / OIDC Identity Provider</h2>
                        <p>Authorization code flow with PKCE. Use these endpoints for OAuth2/OIDC integrations (e.g. Everybody Counts).</p>
                    </div>
                    <div className="endpoint-grid" style={{ marginBottom: '1.5rem' }}>
                        {[
                            { label: 'OpenID Discovery', url: 'https://mydigitalschools.com/.well-known/openid-configuration', description: 'Machine-readable OIDC configuration' },
                            { label: 'Authorization endpoint', url: 'https://mydigitalschools.com/api/oidc/authorize', description: 'Start authorization code flow' },
                            { label: 'Token endpoint', url: 'https://mydigitalschools.com/api/oidc/token', description: 'Exchange code for access_token and id_token' },
                            { label: 'UserInfo endpoint', url: 'https://mydigitalschools.com/api/oidc/userinfo', description: 'Get user claims (Bearer token)' },
                            { label: 'JWKS URI', url: 'https://mydigitalschools.com/api/oidc/jwks', description: 'Public keys for ID token verification' },
                        ].map((endpoint, idx) => (
                            <div key={idx} className="endpoint-card">
                                <div className="endpoint-label">{endpoint.label}</div>
                                <div className="endpoint-url">
                                    <code>{endpoint.url}</code>
                                    <button
                                        className={`copy-btn ${copiedField === endpoint.label ? 'copied' : ''}`}
                                        onClick={() => copyToClipboard(endpoint.url, endpoint.label)}
                                    >
                                        {copiedField === endpoint.label ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}>{endpoint.description}</p>
                            </div>
                        ))}
                    </div>
                    <div className="section-header" style={{ marginTop: '1.5rem' }}>
                        <h3>OIDC claim contract</h3>
                        <p>Claims returned in the ID token and UserInfo response.</p>
                    </div>
                    <table className="attributes-table">
                        <thead>
                            <tr>
                                <th>Claim</th>
                                <th>Format</th>
                                <th>Example</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { name: 'sub', format: 'String (UUID)', example: 'user-uuid', description: 'User ID (stable subject identifier)' },
                                { name: 'email', format: 'String', example: 'user@school.edu', description: 'Primary email' },
                                { name: 'preferred_username', format: 'String', example: 'ic3123', description: 'Login / user_id' },
                                { name: 'name', format: 'String', example: 'John Doe', description: 'Full name' },
                                { name: 'given_name', format: 'String', example: 'John', description: 'First name' },
                                { name: 'family_name', format: 'String', example: 'Doe', description: 'Last name' },
                                { name: 'school_id', format: 'String', example: 'MDS-001', description: 'School identifier' },
                                { name: 'role', format: 'String', example: 'student | teacher | coordinator', description: 'User role' },
                                { name: 'grade', format: 'String', example: '1A', description: 'Grade/section (e.g. 1A, 2B)' },
                                { name: 'section', format: 'String', example: 'A', description: 'Section name when applicable' },
                            ].map((attr, idx) => (
                                <tr key={idx}>
                                    <td><code>{attr.name}</code></td>
                                    <td>{attr.format}</td>
                                    <td>{attr.example}</td>
                                    <td>{attr.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
                        PKCE (S256) is supported. Scopes: <code>openid</code>, <code>profile</code>, <code>email</code>. Callback URLs must be allowlisted per client.
                    </p>
                </div>
            </section>

            {/* Contact */}
            <section className="idp-contact">
                <div className="idp-container">
                    <h2>Need Help?</h2>
                    <p>Our technical team is ready to assist with your SAML or OAuth2/OIDC integration.</p>
                    <a href="mailto:integrations@mydigitalschools.com" className="contact-btn">
                        Contact Integration Team
                    </a>
                </div>
            </section>

            <Footer />
            <ScrollToTop />
        </div>
    );
};

export default IDPIntegration;
